import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageProvider";

type SessionState =
  | { status: "loading" | "signed-out" | "error" }
  | { status: "ready"; userId: string };

/** Mount calendar/voice hooks only after this origin has a session. RLS still
 * authorizes every request. Auth callbacks remain synchronous to avoid locks. */
export function AgendaSessionBoundary({ children }: { children: ReactNode }) {
  const { t } = useLanguage();
  const [state, setState] = useState<SessionState>({ status: "loading" });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let active = true;
    let revision = 0;
    setState({ status: "loading" });
    const applySession = (userId?: string) => {
      setState(userId ? { status: "ready", userId } : { status: "signed-out" });
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!active) return;
      revision += 1;
      applySession(session?.user.id);
    });
    const startedAt = revision;
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!active || revision !== startedAt) return;
      if (error) setState({ status: "error" });
      else applySession(data.session?.user.id);
    }).catch(() => {
      if (active && revision === startedAt) setState({ status: "error" });
    });
    return () => { active = false; subscription.unsubscribe(); };
  }, [attempt]);

  // Switching accounts remounts all calendar state; token refresh for the same
  // user preserves unsaved edits. Signing out unmounts pending calendar work.
  if (state.status === "ready") return <Fragment key={state.userId}>{children}</Fragment>;

  return (
    <main className="min-h-dvh flex items-center justify-center bg-[#050810] px-6 text-white">
      <section className="w-full max-w-sm rounded-2xl border border-white/10 bg-white/5 p-6 text-center">
        {state.status === "loading" ? <p role="status">{t("common.loading")}</p> : <>
          <p role={state.status === "error" ? "alert" : "status"} className="mb-5 text-white/80">
            {t(state.status === "error" ? "ux.agenda.sessionError" : "ux.agenda.signInRequired")}
          </p>
          {state.status === "signed-out" ?
            <Link className="inline-flex min-h-11 items-center rounded-xl bg-cyan-400 px-6 font-medium text-slate-950" to="/auth?mode=signin">{t("auth.signIn")}</Link> :
            <button className="min-h-11 rounded-xl border border-cyan-300/40 px-6 text-cyan-200" onClick={() => setAttempt(value => value + 1)}>{t("common.retry")}</button>}
        </>}
      </section>
    </main>
  );
}
